import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, from, throwError, BehaviorSubject } from 'rxjs';
import { catchError, finalize, switchMap, filter, take, takeUntil } from 'rxjs/operators';
import { SpinnerService } from './spinner.service';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SessionService } from './session.service';
import { AuthService } from './auth.service';
import { CustomSnackbarComponent } from '../components/shared/custom-snackbar/custom-snackbar.component';
import { DialogService } from './dialog.service';
import { RequestCancelService } from './request-cancel.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class MyInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private readonly AI_CHAT_URL = environment.aiChatEndPoint;
  private readonly AI_CHATBOT_URL = environment.aiChatbotEndPoint;
  private readonly AI_ASYNC_INVOKE_URL = environment.aiAsyncInvokeEndPoint;
  private readonly AI_ASYNC_POLL_URL = environment.aiAsyncPollEndPoint;
  private readonly AI_EXECUTE_SQL_URL = environment.aiExecuteSqlEndPoint;

  constructor(
    private spinnerService: SpinnerService, 
    private sessionService: SessionService, 
    private authService: AuthService, 
    private dialogService: DialogService,
    private cancelService: RequestCancelService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {

    // Only JSON - API Like GET POST 
    if(request.responseType!="json") return next.handle(request)

    // Checking User
    if(!this.authService.isLoggedIn()) return next.handle(request);
    

    // Find the Demo User and skip the tokens
    const userType = sessionStorage.getItem('userType');
    if (userType === 'DemoUser') {
      const isFormData = request.body instanceof FormData;
      const headers: { [header: string]: string } = {
        'user_id': this.sessionService.getUsername()
      };

      if (!isFormData) {
        headers['Content-Type'] = 'application/json';
      }

      request = request.clone({
        setHeaders: headers
      });

      return next.handle(request);
    } 

    const session$ = from(fetchAuthSession()).pipe(
      switchMap((session) => {
        if (session) {
         // const token = session.getIdToken().getJwtToken();
          const token = session.tokens?.idToken?.toString() ?? "";
          return this.handleRequestWithToken(request, next, token);
        } else {
          return throwError(() => 'Unauthorized');
        }
      }),
      catchError((error: HttpErrorResponse|any) => {
        if (error.status === 401) {
          return this.handle401Error(request, next)
          .pipe(takeUntil(this.cancelService.cancelSignal$));;
        }

        // when No current user
        if(error=='No current user') {
          this.authService.logoutAndRedirect();
        }
        if(error.message=='No current user') {
          this.authService.logoutAndRedirect();
        }

        if(error.status !== 200) {
          const isOptionalPowerBiRequest =
            !!environment.powerBiApiBaseUrl &&
            request.url.startsWith(environment.powerBiApiBaseUrl);

          if (!isOptionalPowerBiRequest) {
            this.dialogService.customSnackBar('error', 'red', 'Error Message')
          }
        }

        return throwError(() => error);
      }),
      finalize(() => this.spinnerService.hide())
    );

    // Keep floating-chat AI requests alive across module navigation cancellations.
    if (this.isAiChatRequest(request.url)) {
      return session$;
    }

    return session$.pipe(
      takeUntil(this.cancelService.cancelSignal$),
    );
  }

  private isAiChatRequest(url: string): boolean {
    return (
      url === this.AI_CHAT_URL ||
      url === this.AI_CHATBOT_URL ||
      url === this.AI_ASYNC_INVOKE_URL ||
      url === this.AI_ASYNC_POLL_URL ||
      url === this.AI_EXECUTE_SQL_URL
    );
  }

  private handleRequestWithToken(request: HttpRequest<unknown>, next: HttpHandler, token: string): Observable<HttpEvent<unknown>> {


    if (this.authService.isLoggedIn()) {
      // Presigned S3 URLs are self-authenticating — skip all header modifications
      if (request.url.includes('X-Amz-Signature')) {
        this.spinnerService.show();
        return next.handle(request);
      }

      if (request.url === this.AI_CHAT_URL || request.url === this.AI_CHATBOT_URL
        || request.url === this.AI_ASYNC_INVOKE_URL || request.url === this.AI_ASYNC_POLL_URL
        || request.url === this.AI_EXECUTE_SQL_URL) {
        // 🧩 Request for AI Chat endpoints - include Authorization for CORS
        request = request.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

      } else {

        const isFormData = request.body instanceof FormData;

        const headers: { [header: string]: string } = {
          'Authorization': `Bearer ${token}`,
          'user_id': this.sessionService.getUsername()
        };

        // Active schema selection (Primary/Secondary) chosen in the top nav.
        // Only sent when the current page/tab actually has schema checks; if it
        // doesn't, the value is removed from session and no header is attached.
        const schemaType = sessionStorage.getItem('schema_type');
        if (schemaType) {
          headers['schema_type'] = schemaType;
        }

        if (!isFormData) {
          headers['Content-Type'] = 'application/json';
        }
        request = request.clone({
          setHeaders: headers
        });
      }

    }

    this.spinnerService.show();
    return next.handle(request);
  }

  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return from(fetchAuthSession()).pipe(
        switchMap((session) => {
          //const token = session.getIdToken().getJwtToken();
          const token = session.tokens?.idToken?.toString() ?? "";
          this.isRefreshing = false;
          this.refreshTokenSubject.next(token);
          return this.handleRequestWithToken(request, next, token);
        }),
        catchError((error) => {
          // Handle refresh token failure (e.g., redirect to login page)
          return throwError('Unauthorized');
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap(() => {
          return this.handleRequestWithToken(request, next, this.sessionService.getUsername());
        })
      );
    }
  }
}
